import { useReducer, useEffect, useCallback, useRef } from 'react';
import { useApi, API_BASE_URL } from '../../../api/useApi';
import type { CaseListItem, CaseListStatus, CaseStats } from '@lds-efsp/shared-contracts';

interface CasesFilters {
  status?: CaseListStatus;
  search?: string;
  county?: string;
  recentlyFiled?: boolean;
  sort?: 'lastActivity' | 'caseTitle' | 'createdAt';
  page: number;
  perPage: number;
}

interface Pagination {
  page: number;
  perPage: number;
  total: number;
}

interface CasesState {
  cases: CaseListItem[];
  stats: CaseStats | null;
  pagination: Pagination;
  isLoading: boolean;
  error: string | null;
  filters: CasesFilters;
}

type CasesAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; cases: CaseListItem[]; pagination: Pagination; stats: CaseStats }
  | { type: 'FETCH_ERROR'; error: string }
  | { type: 'SET_FILTERS'; filters: Partial<CasesFilters> };

const initialState: CasesState = {
  cases: [],
  stats: null,
  pagination: { page: 1, perPage: 20, total: 0 },
  isLoading: true,
  error: null,
  filters: { page: 1, perPage: 20 },
};

function reducer(state: CasesState, action: CasesAction): CasesState {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, isLoading: true, error: null };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        cases: action.cases,
        stats: action.stats,
        pagination: action.pagination,
        isLoading: false,
        error: null,
      };
    case 'FETCH_ERROR':
      return { ...state, isLoading: false, error: action.error };
    case 'SET_FILTERS':
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.filters,
          page: action.filters.page ?? 1,
        },
      };
    default:
      return state;
  }
}

export function useCases() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { get } = useApi();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const fetchIdRef = useRef(0);

  const fetchCases = useCallback(async (filters: CasesFilters) => {
    const fetchId = ++fetchIdRef.current;
    dispatch({ type: 'FETCH_START' });

    try {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.search) params.set('search', filters.search);
      if (filters.county) params.set('county', filters.county);
      if (filters.recentlyFiled) params.set('recentlyFiled', 'true');
      if (filters.sort) params.set('sort', filters.sort);
      params.set('page', String(filters.page));
      params.set('perPage', String(filters.perPage));

      const qs = params.toString();
      const [listResult, statsResult] = await Promise.all([
        get<{ data: CaseListItem[]; pagination: Pagination }>(`${API_BASE_URL}/api/cases?${qs}`),
        get<CaseStats>(`${API_BASE_URL}/api/cases/stats`),
      ]);

      if (fetchId !== fetchIdRef.current) return;

      dispatch({
        type: 'FETCH_SUCCESS',
        cases: listResult.data,
        pagination: listResult.pagination,
        stats: statsResult,
      });
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return;
      dispatch({
        type: 'FETCH_ERROR',
        error: err instanceof Error ? err.message : 'Failed to load cases',
      });
    }
  }, [get]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchCases(state.filters);
    }, state.filters.search ? 300 : 0);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [state.filters, fetchCases]);

  const setFilters = useCallback((filters: Partial<CasesFilters>) => {
    dispatch({ type: 'SET_FILTERS', filters });
  }, []);

  const refetch = useCallback(() => {
    fetchCases(state.filters);
  }, [fetchCases, state.filters]);

  return {
    cases: state.cases,
    stats: state.stats,
    pagination: state.pagination,
    isLoading: state.isLoading,
    error: state.error,
    filters: state.filters,
    setFilters,
    refetch,
  };
}
